/**
 * Non-plot states shared by every chart in ChartsComponent: loading, error,
 * empty, the render-error boundary, and the data-quality notice.
 *
 * These are deliberately inline-sized (they fill the chart box, not the screen)
 * so a failing chart never blanks the page around it. Chart-specific chrome
 * (legends, tables, tooltips) lives in each chart's own folder, because its
 * shape follows the data model rather than the state machine.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, LineChart, PieChart, RefreshCw, TriangleAlert } from "lucide-react";
import { cn } from "../ui/utils";

/** Which skeleton and empty-state glyph to draw: radial charts or cartesian. */
export type ChartShape = "radial" | "cartesian";

/* ------------------------------------------------------------------ */
/* Status states                                                       */
/* ------------------------------------------------------------------ */

interface StateShellProps {
  height: number;
  children: ReactNode;
  className?: string;
}

function StateShell({ height, children, className }: StateShellProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-2 px-4 py-6 text-center",
        className,
      )}
      style={{ minHeight: height }}
    >
      {children}
    </div>
  );
}

/**
 * Pulsing placeholder that holds the chart's footprint while data loads, so the
 * surrounding layout does not jump when the real chart arrives. The skeleton
 * mirrors the shape of the chart it stands in for.
 */
export function ChartLoadingState({
  height,
  shape = "radial",
}: {
  height: number;
  shape?: ChartShape;
}) {
  return (
    <StateShell height={height}>
      {shape === "radial" ? (
        <div className="flex w-full animate-pulse flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <div
            className="shrink-0 rounded-full bg-muted"
            style={{ width: height * 0.62, height: height * 0.62 }}
          />
          <div className="w-full max-w-[180px] space-y-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="size-2.5 shrink-0 rounded-full bg-muted" />
                <div className="h-3 flex-1 rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex w-full animate-pulse flex-col gap-3">
          <div className="flex items-center justify-center gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="size-2.5 rounded-full bg-muted" />
                <div className="h-2.5 w-12 rounded bg-muted" />
              </div>
            ))}
          </div>
          {/* Bars of differing heights read as a plot area rather than a table. */}
          <div className="flex items-end gap-1.5" style={{ height: height * 0.6 }}>
            {[0.45, 0.7, 0.35, 0.85, 0.55, 0.95, 0.4, 0.75, 0.6, 0.5].map((scale, i) => (
              <div
                key={i}
                className="flex-1 rounded-t bg-muted"
                style={{ height: `${scale * 100}%` }}
              />
            ))}
          </div>
          <div className="h-2.5 w-full rounded bg-muted" />
        </div>
      )}
      <span className="sr-only">Loading chart data</span>
    </StateShell>
  );
}

interface ChartErrorStateProps {
  height: number;
  message: string;
  onRetry?: () => void;
}

/** Inline failure state. Shows the reason plus a retry when the caller can. */
export function ChartErrorState({ height, message, onRetry }: ChartErrorStateProps) {
  return (
    <StateShell height={height}>
      <div className="flex size-11 items-center justify-center rounded-2xl bg-red-100 dark:bg-red-500/15">
        <AlertTriangle className="size-5 text-red-600 dark:text-red-400" />
      </div>
      <p className="text-sm font-semibold text-foreground">Chart could not be displayed</p>
      <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RefreshCw className="size-3.5" />
          Try again
        </button>
      )}
    </StateShell>
  );
}

/** Shown when there is nothing plottable: no rows, or every value is zero. */
export function ChartEmptyState({
  height,
  message,
  shape = "radial",
}: {
  height: number;
  message: string;
  shape?: ChartShape;
}) {
  const Glyph = shape === "radial" ? PieChart : LineChart;
  return (
    <StateShell height={height}>
      <div className="flex size-11 items-center justify-center rounded-2xl bg-muted/60">
        <Glyph className="size-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-semibold text-foreground">No data to display</p>
      <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{message}</p>
    </StateShell>
  );
}

/* ------------------------------------------------------------------ */
/* Error boundary                                                      */
/* ------------------------------------------------------------------ */

interface BoundaryProps {
  height: number;
  children: ReactNode;
  onRetry?: () => void;
}

interface BoundaryState {
  error: Error | null;
}

/**
 * Contains a render crash to the chart box.
 *
 * The app-level ErrorBoundary is full-screen, so a chart that throws on odd
 * data would take the whole page with it. This one keeps the surrounding page
 * intact and offers a local reset.
 */
export class ChartErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Chart render failed:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.error) {
      return (
        <ChartErrorState
          height={this.props.height}
          message={this.state.error?.message || "An unexpected error occurred while rendering."}
          onRetry={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/* Data-quality notice                                                 */
/* ------------------------------------------------------------------ */

/** Quiet note listing rows that were dropped, merged, or folded into "Other". */
export function ChartWarnings({ warnings }: { warnings: string[] }) {
  if (!warnings?.length) return null;
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 dark:bg-amber-500/10">
      <TriangleAlert className="mt-[1px] size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <ul className="min-w-0 space-y-0.5">
        {warnings.map((warning, i) => (
          <li
            key={i}
            className="break-words text-[10px] leading-snug text-amber-800 dark:text-amber-200"
          >
            {warning}
          </li>
        ))}
      </ul>
    </div>
  );
}
